import { AdSlotEvent, AdSlotType } from '../../advertising';
import { AdvertisingEvent } from '../../advertising/event-types';
import { PlayerEvent } from '../../event-emitter/events';
import { PlayerInterface } from '../../player-interface';

/**
 * This is the playerInterface with the properties we actually need in
 * the PlaybackSpeedManager class. This hopefully helps in tests with
 * limiting the amount of 'mocking' needed.
 */
type PartialPlayerInterface = Pick<
  PlayerInterface,
  'addEventListener' | 'getPlaybackSpeed' | 'setPlaybackSpeed' | 'getMedia'
>;

const DEFAULT_PLAYBACK_SPEED = 1.0;

/**
 * This class is used to encapsulate the functionality of everything related to
 * the playbackSpeed setting.
 */
export class PlaybackSpeedObserver {
  private contentPlaybackSpeed: number;
  private readonly player: PartialPlayerInterface;

  constructor(player: PartialPlayerInterface) {
    this.player = player;
    this.contentPlaybackSpeed = player.getPlaybackSpeed();

    // The player has loaded the media and is ready for playback. There
    // are root players that only now accept the manipulation of the
    // playback speed.
    player.addEventListener(PlayerEvent.MediaLoaded, () => {
      this.contentPlaybackSpeed = this.getInitialPlaybackSpeed();
      player.setPlaybackSpeed(this.contentPlaybackSpeed);
    });

    // We don't want the advertisement slot to be played in another speed
    // than 'normal' which equals 1.0. Also, playbackSpeed changes during
    // an AdSlot is permitted.
    // However, the playback speed should NOT be reset for a non-linear ad.
    player.addEventListener(AdvertisingEvent.AdSlotStarted, (event: AdSlotEvent) => {
      if (event.adSlot.getType() === AdSlotType.NonLinear) {
        return;
      }

      this.contentPlaybackSpeed = player.getPlaybackSpeed();
      player.setPlaybackSpeed(DEFAULT_PLAYBACK_SPEED);
    });

    // We don't want the advertising slot to interfere with the user setting.
    player.addEventListener(AdvertisingEvent.AdSlotCompleted, (event: AdSlotEvent) => {
      // The playback speed should NOT be reset for a non-linear ad.
      if (event.adSlot.getType() === AdSlotType.NonLinear) {
        return;
      }

      player.setPlaybackSpeed(this.contentPlaybackSpeed);
    });
  }

  getInitialPlaybackSpeed() {
    return this.player.getMedia()?.playbackSpeed ?? DEFAULT_PLAYBACK_SPEED;
  }
}
